import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  IconButton,
  Divider,
  Tooltip,
  makeStyles,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import RefreshIcon from "@material-ui/icons/Refresh";
import VisibilityIcon from "@material-ui/icons/Visibility";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import BlockIcon from "@material-ui/icons/Block";
import ReceiptIcon from "@material-ui/icons/Receipt";
import axios from "axios";
import { format, parseISO } from "date-fns";
import DashboardAdmin from "../DashboardAdmin";
import useUserState from "../../hooks/useUserState";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  statsGrid: {
    marginBottom: theme.spacing(3),
  },
  statCard: {
    padding: theme.spacing(2),
    textAlign: "center",
    borderTop: `3px solid ${theme.palette.divider}`,
  },
  statValue: {
    fontWeight: 700,
  },
  filtersPaper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  tableContainer: {
    backgroundColor: "#fff",
    borderRadius: theme.shape.borderRadius,
    boxShadow: "0 0 10px rgba(0, 0, 0, 0.08)",
  },
  emptyState: {
    padding: theme.spacing(6),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  dialogPaper: {
    minWidth: "480px",
  },
  detailRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: theme.spacing(0.75, 0),
  },
  detailLabel: {
    color: theme.palette.text.secondary,
  },
  sectionTitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
}));

const STATUS_LABELS = {
  pending_payment: "Pendiente de pago",
  payment_confirmed: "Pago confirmado",
  in_process: "En proceso",
  issued: "Emitido",
  rejected: "Rechazado",
};

const STATUS_COLORS = {
  pending_payment: { bg: "#fff3e0", color: "#e65100" },
  payment_confirmed: { bg: "#e3f2fd", color: "#1565c0" },
  in_process: { bg: "#ede7f6", color: "#5e35b1" },
  issued: { bg: "#e8f5e9", color: "#2e7d32" },
  rejected: { bg: "#ffebee", color: "#c62828" },
};

const PAYMENT_STATUS_LABELS = {
  pending: "Pendiente",
  payed: "Pagado",
  declined: "Rechazado",
};

const PAYMENT_STATUS_COLORS = {
  pending: { bg: "#fff3e0", color: "#e65100" },
  payed: { bg: "#e8f5e9", color: "#2e7d32" },
  declined: { bg: "#ffebee", color: "#c62828" },
};

const StatusChip = ({ value, labels, colors }) => {
  const style = colors[value] || { bg: "#eeeeee", color: "#424242" };
  return (
    <Chip
      label={labels[value] || value}
      size="small"
      style={{
        backgroundColor: style.bg,
        color: style.color,
        fontWeight: 600,
      }}
    />
  );
};

export default function DriverMedicalCertificateAdminList() {
  const classes = useStyles();
  const [userState] = useUserState();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ status: "", payment_status: "" });

  const [detail, setDetail] = useState(null);
  const [dialogMode, setDialogMode] = useState(null); // "issue" | "reject"
  const [issueFile, setIssueFile] = useState(null);
  const [rejectNotes, setRejectNotes] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const apiBase = process.env.REACT_APP_API_URL_BASE;
  const authHeaders = { Authorization: `Bearer ${userState?.jwt}` };

  const fetchRequests = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.payment_status) params.payment_status = filters.payment_status;

      const response = await axios.get(
        `${apiBase}/admin/driver-medical-certificate`,
        { headers: authHeaders, params }
      );
      setRequests(response.data);
    } catch (error) {
      setError("No se pudieron cargar las solicitudes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.payment_status]);

  const stats = useMemo(
    () => ({
      total: requests.length,
      pendingPayment: requests.filter((r) => r.payment_status === "pending")
        .length,
      readyToIssue: requests.filter(
        (r) => r.payment_status === "payed" && r.status !== "issued" && r.status !== "rejected"
      ).length,
      issued: requests.filter((r) => r.status === "issued").length,
    }),
    [requests]
  );

  const openDetail = (request) => setDetail(request);
  const closeDetail = () => setDetail(null);

  const openIssueDialog = (request) => {
    setDetail(request);
    setIssueFile(null);
    setDialogMode("issue");
  };

  const openRejectDialog = (request) => {
    setDetail(request);
    setRejectNotes("");
    setDialogMode("reject");
  };

  const closeDialog = () => {
    setDialogMode(null);
    setIssueFile(null);
    setRejectNotes("");
  };

  const handleIssue = async () => {
    if (!issueFile) {
      setError("Selecciona el archivo PDF del dictamen");
      return;
    }
    try {
      setActionLoading(true);
      const formData = new FormData();
      formData.append("file", issueFile);

      await axios.patch(
        `${apiBase}/admin/driver-medical-certificate/${detail.id}/issue`,
        formData,
        { headers: authHeaders }
      );
      closeDialog();
      setDetail(null);
      fetchRequests();
    } catch (error) {
      setError("No se pudo emitir el dictamen");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    try {
      setActionLoading(true);
      await axios.patch(
        `${apiBase}/admin/driver-medical-certificate/${detail.id}/reject`,
        { admin_notes: rejectNotes },
        { headers: authHeaders }
      );
      closeDialog();
      setDetail(null);
      fetchRequests();
    } catch (error) {
      setError("No se pudo rechazar la solicitud");
    } finally {
      setActionLoading(false);
    }
  };

  const canManage = (request) =>
    request.status !== "issued" && request.status !== "rejected";

  return (
    <DashboardAdmin>
      <div className={classes.root}>
        <div className={classes.header}>
          <Typography variant="h4">Solicitudes de Dictamen Médico</Typography>
          <Tooltip title="Actualizar">
            <IconButton onClick={fetchRequests} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </div>

        <Grid container spacing={2} className={classes.statsGrid}>
          <Grid item xs={6} sm={3}>
            <Paper className={classes.statCard}>
              <Typography variant="h5" className={classes.statValue}>
                {stats.total}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Total
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper
              className={classes.statCard}
              style={{ borderTopColor: "#e65100" }}
            >
              <Typography variant="h5" className={classes.statValue}>
                {stats.pendingPayment}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Pendientes de pago
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper
              className={classes.statCard}
              style={{ borderTopColor: "#1565c0" }}
            >
              <Typography variant="h5" className={classes.statValue}>
                {stats.readyToIssue}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Listas para emitir
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper
              className={classes.statCard}
              style={{ borderTopColor: "#2e7d32" }}
            >
              <Typography variant="h5" className={classes.statValue}>
                {stats.issued}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Emitidas
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Paper className={classes.filtersPaper}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Estado</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, status: e.target.value }))
                  }
                  label="Estado"
                >
                  <MenuItem value="">Todos</MenuItem>
                  {Object.entries(STATUS_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Pago</InputLabel>
                <Select
                  value={filters.payment_status}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      payment_status: e.target.value,
                    }))
                  }
                  label="Pago"
                >
                  <MenuItem value="">Todos</MenuItem>
                  {Object.entries(PAYMENT_STATUS_LABELS).map(
                    ([value, label]) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {error && (
          <Alert
            severity="error"
            onClose={() => setError(null)}
            style={{ marginBottom: 16 }}
          >
            {error}
          </Alert>
        )}

        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Titular</TableCell>
                <TableCell>Cédula</TableCell>
                <TableCell>Licencia</TableCell>
                <TableCell>Fecha</TableCell>
                <TableCell>Pago</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((request) => (
                <TableRow key={request.id} hover>
                  <TableCell>#{request.id}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {request.full_name}
                    </Typography>
                    {request.is_third_party && (
                      <Typography variant="caption" color="textSecondary">
                        Solicitado para un tercero
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{request.document_number}</TableCell>
                  <TableCell>{request.license_type}</TableCell>
                  <TableCell>
                    {request.created_at
                      ? format(parseISO(request.created_at), "dd/MM/yyyy HH:mm")
                      : "N/A"}
                  </TableCell>
                  <TableCell>
                    <StatusChip
                      value={request.payment_status}
                      labels={PAYMENT_STATUS_LABELS}
                      colors={PAYMENT_STATUS_COLORS}
                    />
                  </TableCell>
                  <TableCell>
                    <StatusChip
                      value={request.status}
                      labels={STATUS_LABELS}
                      colors={STATUS_COLORS}
                    />
                    {request.requires_electronic_invoice && (
                      <Tooltip title="Requiere factura electrónica">
                        <ReceiptIcon
                          fontSize="small"
                          style={{ marginLeft: 8, verticalAlign: "middle", color: "#8e24aa" }}
                        />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Ver detalle">
                      <IconButton size="small" onClick={() => openDetail(request)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {canManage(request) && request.payment_status === "payed" && (
                      <Tooltip title="Subir dictamen">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => openIssueDialog(request)}
                        >
                          <CloudUploadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canManage(request) && (
                      <Tooltip title="Rechazar">
                        <IconButton
                          size="small"
                          onClick={() => openRejectDialog(request)}
                          style={{ color: "#c62828" }}
                        >
                          <BlockIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {!loading && requests.length === 0 && (
            <div className={classes.emptyState}>
              <Typography variant="body1">
                No hay solicitudes que coincidan con los filtros seleccionados.
              </Typography>
            </div>
          )}

          {loading && (
            <div className={classes.emptyState}>
              <CircularProgress size={28} />
            </div>
          )}
        </TableContainer>

        {/* ---- Detail dialog ---- */}
        <Dialog
          open={!!detail && !dialogMode}
          onClose={closeDetail}
          fullWidth
          classes={{ paper: classes.dialogPaper }}
        >
          {detail && (
            <>
              <DialogTitle>Solicitud #{detail.id}</DialogTitle>
              <DialogContent>
                <Typography variant="subtitle2" className={classes.sectionTitle}>
                  Datos del titular
                </Typography>
                <Divider />
                {[
                  ["Nombre completo", detail.full_name],
                  ["Cédula", detail.document_number],
                  ["Edad", detail.age],
                  ["Teléfono", detail.phone],
                  ["Correo", detail.email],
                  ["Tipo de licencia", detail.license_type],
                  ["Estatura (cm)", detail.height_cm],
                  ["Peso (kg)", detail.weight_kg],
                  ["Donador de órganos", detail.is_organ_donor ? "Sí" : "No"],
                  [
                    "Lateralidad",
                    detail.handedness === "right" ? "Diestro" : "Zurdo",
                  ],
                  ["Solicitado para un tercero", detail.is_third_party ? "Sí" : "No"],
                ].map(([label, value]) => (
                  <div className={classes.detailRow} key={label}>
                    <Typography variant="body2" className={classes.detailLabel}>
                      {label}
                    </Typography>
                    <Typography variant="body2">{value ?? "N/A"}</Typography>
                  </div>
                ))}

                {detail.requires_electronic_invoice && (
                  <>
                    <Typography
                      variant="subtitle2"
                      className={classes.sectionTitle}
                    >
                      Facturación electrónica
                    </Typography>
                    <Divider />
                    {[
                      ["Razón social", detail.billing_name],
                      ["Cédula jurídica/fiscal", detail.billing_tax_id],
                      ["Correo de facturación", detail.billing_email],
                      ["Dirección", detail.billing_address],
                    ].map(([label, value]) => (
                      <div className={classes.detailRow} key={label}>
                        <Typography
                          variant="body2"
                          className={classes.detailLabel}
                        >
                          {label}
                        </Typography>
                        <Typography variant="body2">{value || "N/A"}</Typography>
                      </div>
                    ))}
                  </>
                )}

                <Typography variant="subtitle2" className={classes.sectionTitle}>
                  Pago y estado
                </Typography>
                <Divider />
                <div className={classes.detailRow}>
                  <Typography variant="body2" className={classes.detailLabel}>
                    Precio total
                  </Typography>
                  <Typography variant="body2">${detail.price_total}</Typography>
                </div>
                <div className={classes.detailRow}>
                  <Typography variant="body2" className={classes.detailLabel}>
                    Estado de pago
                  </Typography>
                  <StatusChip
                    value={detail.payment_status}
                    labels={PAYMENT_STATUS_LABELS}
                    colors={PAYMENT_STATUS_COLORS}
                  />
                </div>
                <div className={classes.detailRow}>
                  <Typography variant="body2" className={classes.detailLabel}>
                    Estado de solicitud
                  </Typography>
                  <StatusChip
                    value={detail.status}
                    labels={STATUS_LABELS}
                    colors={STATUS_COLORS}
                  />
                </div>
                {detail.admin_notes && (
                  <div className={classes.detailRow}>
                    <Typography variant="body2" className={classes.detailLabel}>
                      Notas del admin
                    </Typography>
                    <Typography variant="body2">{detail.admin_notes}</Typography>
                  </div>
                )}
              </DialogContent>
              <DialogActions>
                {canManage(detail) && detail.payment_status === "payed" && (
                  <Button
                    color="primary"
                    onClick={() => setDialogMode("issue")}
                  >
                    Subir Dictamen
                  </Button>
                )}
                {canManage(detail) && (
                  <Button
                    style={{ color: "#c62828" }}
                    onClick={() => setDialogMode("reject")}
                  >
                    Rechazar
                  </Button>
                )}
                <Button onClick={closeDetail}>Cerrar</Button>
              </DialogActions>
            </>
          )}
        </Dialog>

        {/* ---- Issue dialog ---- */}
        <Dialog open={dialogMode === "issue"} onClose={closeDialog} fullWidth>
          <DialogTitle>Subir Dictamen Médico</DialogTitle>
          <DialogContent>
            <Typography variant="body2" gutterBottom>
              Solicitud #{detail?.id} - {detail?.full_name}
            </Typography>
            <input
              type="file"
              accept="application/pdf"
              onChange={(e) => setIssueFile(e.target.files[0])}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog} disabled={actionLoading}>
              Cancelar
            </Button>
            <Button
              color="primary"
              variant="contained"
              onClick={handleIssue}
              disabled={actionLoading}
            >
              {actionLoading ? <CircularProgress size={20} /> : "Enviar"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ---- Reject dialog ---- */}
        <Dialog open={dialogMode === "reject"} onClose={closeDialog} fullWidth>
          <DialogTitle>Rechazar Solicitud</DialogTitle>
          <DialogContent>
            <Typography variant="body2" gutterBottom>
              Solicitud #{detail?.id} - {detail?.full_name}
            </Typography>
            <TextField
              label="Motivo / notas"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value={rejectNotes}
              onChange={(e) => setRejectNotes(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog} disabled={actionLoading}>
              Cancelar
            </Button>
            <Button
              style={{ color: "#c62828" }}
              variant="outlined"
              onClick={handleReject}
              disabled={actionLoading}
            >
              {actionLoading ? <CircularProgress size={20} /> : "Rechazar"}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </DashboardAdmin>
  );
}
